import { memo, useCallback, useMemo } from "react"

import { DateRange } from "react-day-picker"

import DatePicker from "components/DatePicker/DatePicker"
import { DatePickerMode, DatePickerValue } from "components/DatePicker/DatePicker.types"

import { DatePickerFilterProps } from "./types"

export const DatePickerFilter = memo(({ label, onApply, initialValue }: DatePickerFilterProps) => {
    const initialDateRange = useMemo(() => {
        if (!initialValue) {
            return undefined
        }

        try {
            const parsed = JSON.parse(initialValue)

            // Extract only the date part (YYYY-MM-DD) to avoid timezone shifts
            const parseDateOnly = (dateStr: string) => {
                const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr
                return new Date(dateOnly + 'T00:00:00')
            }

            return {
                from: parsed.startDate ? parseDateOnly(parsed.startDate) : undefined,
                to: parsed.endDate ? parseDateOnly(parsed.endDate) : undefined
            }
        } catch {
            return undefined
        }
    }, [initialValue])

    const handleApply = useCallback((value: DatePickerValue) => {
        if (!value) {
            onApply('')
            return
        }

        const dateRange = value as DateRange

        if (!dateRange.from) {
            onApply('')
            return
        }

        const startDate = dateRange.from
        const endDate = dateRange.to || dateRange.from

        // Format date as YYYY-MM-DD in LOCAL timezone (not UTC to avoid timezone shifts)
        const formatLocalDate = (date: Date) => {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }

        const result = JSON.stringify({
            startDate: formatLocalDate(startDate),
            endDate: formatLocalDate(endDate)
        })
        onApply(result)
    }, [onApply])

    const handleClear = useCallback(() => {
        onApply('')
    }, [onApply])

    return (
        <div className="flex flex-col rounded-md">
            <DatePicker
                mode={DatePickerMode.RANGE}
                numberOfMonths={1}
                shouldDisplayTime={false}
                value={initialDateRange}
                onApply={handleApply}
                onClear={handleClear}
                shouldExcludeFuture={true}
                label={label}
                placeholder="Select date range"
                alwaysOpen={true}
                hideTrigger={true}
                popoverClassName="p-2"
            />
        </div>
    )
})

DatePickerFilter.displayName = 'DatePickerFilter'

